import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { Badge, EmptyState, StatCard } from "@/components/DashUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { rupee, formatApiErrorDetail, API } from "@/lib/api";
import { Copy, Share2, QrCode, Wallet, Send, Users, Loader2, Link2 } from "lucide-react";

const TABS = [
  { k: "overview", label: "Overview" },
  { k: "referrals", label: "My Referrals" },
  { k: "payouts", label: "Payouts" },
  { k: "settings", label: "Settings" },
];

export default function PartnerDashboard() {
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [dash, setDash] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const origin = window.location.origin;

  const loadAll = async () => {
    const [m, d, r, p] = await Promise.all([
      api.get("/partner/me"), api.get("/partner/dashboard"),
      api.get("/partner/referrals"), api.get("/partner/payouts"),
    ]);
    setMe(m.data); setDash(d.data); setReferrals(r.data); setPayouts(p.data);
  };
  useEffect(() => { loadAll().catch(() => {}); }, []);

  const link = me ? `${origin}/refer/${me.code}` : "";
  const copy = (txt, label) => { navigator.clipboard.writeText(txt); toast.success(`${label} copied!`); };
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(link)}`;

  if (!me || !dash) return <DashboardShell title="Partner"><div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-violet-400 animate-spin" /></div></DashboardShell>;

  return (
    <DashboardShell title="Partner Dashboard" tabs={TABS} active={tab} onTab={setTab}>
      {tab === "overview" && (
        <div className="space-y-6">
          <div className="glass-strong rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-xs font-mono uppercase text-cyan-300 mb-1">Your referral link</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-white bg-white/5 rounded-lg px-3 py-2 text-sm" data-testid="partner-ref-link">{link}</code>
                  <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10" onClick={() => copy(link, "Link")} data-testid="copy-link"><Copy className="w-4 h-4 mr-1.5" />Copy Link</Button>
                  <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10" onClick={() => copy(me.code, "Code")} data-testid="copy-code"><Copy className="w-4 h-4 mr-1.5" />Copy Code ({me.code})</Button>
                  <a href={`https://wa.me/?text=${encodeURIComponent("Check out Nova Web Solutions for a premium website: " + link)}`} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="share-whatsapp"><Share2 className="w-4 h-4 mr-1.5" />WhatsApp</Button>
                  </a>
                  <Dialog>
                    <DialogTrigger asChild><Button size="sm" variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/10" data-testid="show-qr"><QrCode className="w-4 h-4 mr-1.5" />QR</Button></DialogTrigger>
                    <DialogContent className="glass-strong border-white/10 text-white max-w-xs">
                      <DialogHeader><DialogTitle>Referral QR Code</DialogTitle></DialogHeader>
                      <img src={qrUrl} alt="QR" className="rounded-xl mx-auto bg-white p-2" />
                      <a href={qrUrl} download="nova-referral-qr.png"><Button className="w-full bg-violet-600 hover:bg-violet-500 text-white">Download QR</Button></a>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-total" label="Total Referrals" value={dash.total_referrals} accent="violet" />
            <StatCard testid="stat-qualified" label="Qualified" value={dash.qualified_referrals} accent="cyan" />
            <StatCard testid="stat-converted" label="Converted Clients" value={dash.converted_clients} accent="emerald" />
            <StatCard testid="stat-conversion" label="Conversion Rate" value={`${dash.conversion_rate}%`} sub={dash.conversion_denominator} accent="slate" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard testid="stat-pending" label="Pending" value={rupee(dash.pending_commission_rupees)} accent="amber" />
            <StatCard testid="stat-approved" label="Approved Unpaid" value={rupee(dash.approved_unpaid_rupees)} accent="cyan" />
            <StatCard testid="stat-held" label="Held in Payouts" value={rupee(dash.held_in_payouts_rupees)} accent="slate" />
            <StatCard testid="stat-available" label="Available to Withdraw" value={rupee(dash.available_rupees)} accent="emerald" />
            <StatCard testid="stat-paid" label="Paid" value={rupee(dash.paid_rupees)} accent="violet" />
          </div>

          <ManualReferralForm onDone={loadAll} />
        </div>
      )}

      {tab === "referrals" && (
        <div className="glass-strong rounded-2xl p-6 overflow-x-auto" data-testid="partner-referrals-table">
          {referrals.length === 0 ? <EmptyState icon={Users} title="No referrals yet" hint="Submit your first referral from the Overview tab." /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-white/10">
                <th className="py-3 px-2">Business</th><th className="px-2">Submitted</th><th className="px-2">Status</th><th className="px-2">Commission</th><th className="px-2">Commission Status</th><th className="px-2">Last Update</th>
              </tr></thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 text-slate-200">
                    <td className="py-3 px-2 font-medium">{r.business_name}</td>
                    <td className="px-2 text-slate-400 font-mono text-xs">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-2"><Badge status={r.status} /></td>
                    <td className="px-2 font-mono">{r.commission_rupees != null ? rupee(r.commission_rupees) : "—"}</td>
                    <td className="px-2">{r.commission_status ? <Badge status={r.commission_status} /> : <span className="text-slate-500">—</span>}</td>
                    <td className="px-2 text-slate-400 font-mono text-xs">{(r.updated_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "payouts" && <PayoutsTab dash={dash} payouts={payouts} me={me} onDone={loadAll} />}
      {tab === "settings" && <SettingsTab me={me} onDone={loadAll} />}
    </DashboardShell>
  );
}

function ManualReferralForm({ onDone }) {
  const [f, setF] = useState({ business_name: "", contact_person: "", phone: "", category: "", location: "", notes: "", consent: false });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.consent) return toast.error("Please confirm the business agreed to be contacted.");
    setLoading(true);
    try { await api.post("/partner/referrals", f); toast.success("Referral submitted!"); setF({ business_name: "", contact_person: "", phone: "", category: "", location: "", notes: "", consent: false }); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const fld = "bg-white/5 border-white/10 text-white";
  return (
    <div className="glass-strong rounded-2xl p-6" data-testid="manual-referral-form">
      <h3 className="font-display text-lg font-bold text-white mb-4">Submit a referral</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Business name" className={fld} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="ref-business" />
        <Input placeholder="Contact person" className={fld} value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} data-testid="ref-contact" />
        <Input placeholder="Business phone" className={fld} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="ref-phone" />
        <Input placeholder="Business category" className={fld} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ref-category" />
        <Input placeholder="Location (optional)" className={fld} value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
        <Input placeholder="Notes (optional)" className={fld} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </div>
      <label className="flex items-center gap-2.5 text-sm text-slate-300 mt-3"><Checkbox checked={f.consent} onCheckedChange={(c) => setF({ ...f, consent: !!c })} data-testid="ref-consent" /> I confirm this business agreed to be contacted.</label>
      <Button className="mt-4 bg-violet-600 hover:bg-violet-500 text-white" onClick={submit} disabled={loading || !f.business_name || !f.phone} data-testid="ref-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Submit Referral</>}
      </Button>
    </div>
  );
}

function PayoutsTab({ dash, payouts, me, onDone }) {
  const [amount, setAmount] = useState(dash.available_rupees || 0);
  const [loading, setLoading] = useState(false);
  const request = async () => {
    setLoading(true);
    try { const { data } = await api.post("/partner/payouts", { amount_rupees: Number(amount) }); toast.success(`Payout requested: ${rupee(data.reserved_rupees)}`); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const cancel = async (id) => { try { await api.post(`/partner/payouts/${id}/cancel`); toast.success("Payout cancelled."); onDone(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6">
      <div className="glass-strong rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><Wallet className="w-5 h-5 text-emerald-400" /><h3 className="font-display text-lg font-bold text-white">Request a withdrawal</h3></div>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <StatCard label="Available" value={rupee(dash.available_rupees)} accent="emerald" />
          <StatCard label="Held in requests" value={rupee(dash.held_in_payouts_rupees)} accent="slate" />
          <StatCard label="Min payout" value={rupee(dash.payout_threshold_rupees)} accent="amber" />
        </div>
        {!me.has_payout_details && <p className="text-sm text-amber-300/90 mb-3">Add your payout details in Settings before requesting.</p>}
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label className="text-slate-200 mb-1.5 block">Amount (₹)</Label><Input type="number" className="bg-white/5 border-white/10 text-white w-40" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payout-amount" /></div>
          <Button className="bg-violet-600 hover:bg-violet-500 text-white" onClick={request} disabled={loading || !me.has_payout_details} data-testid="payout-request">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Withdrawal"}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">The requested amount is reserved from your approved balance to prevent overlapping withdrawals.</p>
      </div>

      <div className="glass-strong rounded-2xl p-6 overflow-x-auto" data-testid="partner-payouts-table">
        <h3 className="font-semibold text-white mb-4">Payout history</h3>
        {payouts.length === 0 ? <EmptyState icon={Wallet} title="No payout requests yet" /> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-white/10"><th className="py-3 px-2">Amount</th><th className="px-2">Status</th><th className="px-2">Reference</th><th className="px-2">Requested</th><th className="px-2"></th></tr></thead>
            <tbody>{payouts.map((p) => (
              <tr key={p.id} className="border-b border-white/5 text-slate-200">
                <td className="py-3 px-2 font-mono">{rupee(p.amount_rupees)}</td>
                <td className="px-2"><Badge status={p.state} /></td>
                <td className="px-2 text-slate-400">{p.reference || "—"}</td>
                <td className="px-2 text-slate-400 font-mono text-xs">{(p.created_at || "").slice(0, 10)}</td>
                <td className="px-2">{["requested", "under_review"].includes(p.state) && <Button size="sm" variant="ghost" className="text-red-300 hover:bg-red-500/10" onClick={() => cancel(p.id)} data-testid={`cancel-payout-${p.id}`}>Cancel</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ me, onDone }) {
  const [pd, setPd] = useState({ method: "upi", account_name: "", upi_id: "", account_number: "", ifsc: "" });
  const [lb, setLb] = useState({ opt_in: me.leaderboard_opt_in, display_name: me.display_name || "" });
  const fld = "bg-white/5 border-white/10 text-white";
  const savePd = async () => { try { await api.put("/partner/payout-details", pd); toast.success("Payout details saved securely."); onDone(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const saveLb = async () => { try { await api.put("/partner/leaderboard", lb); toast.success("Leaderboard preference saved."); onDone(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass-strong rounded-2xl p-6" data-testid="payout-details-form">
        <h3 className="font-display text-lg font-bold text-white mb-1">Payout details</h3>
        <p className="text-xs text-slate-500 mb-4">Stored privately. We never ask for UPI PINs, bank passwords or OTPs.</p>
        <div className="space-y-3">
          <Select value={pd.method} onValueChange={(v) => setPd({ ...pd, method: v })}>
            <SelectTrigger className={fld}><SelectValue /></SelectTrigger>
            <SelectContent className="glass-strong border-white/10 text-white"><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem></SelectContent>
          </Select>
          <Input placeholder="Account holder name" className={fld} value={pd.account_name} onChange={(e) => setPd({ ...pd, account_name: e.target.value })} />
          {pd.method === "upi" ? (
            <Input placeholder="UPI ID (e.g. name@bank)" className={fld} value={pd.upi_id} onChange={(e) => setPd({ ...pd, upi_id: e.target.value })} data-testid="payout-upi" />
          ) : (<>
            <Input placeholder="Account number" className={fld} value={pd.account_number} onChange={(e) => setPd({ ...pd, account_number: e.target.value })} />
            <Input placeholder="IFSC" className={fld} value={pd.ifsc} onChange={(e) => setPd({ ...pd, ifsc: e.target.value })} />
          </>)}
          <Button className="bg-violet-600 hover:bg-violet-500 text-white" onClick={savePd} data-testid="save-payout-details">Save details</Button>
        </div>
      </div>

      <div className="glass-strong rounded-2xl p-6">
        <h3 className="font-display text-lg font-bold text-white mb-1">Public leaderboard</h3>
        <p className="text-xs text-slate-500 mb-4">Opt in to appear on the public leaderboard. Only your display name and converted count are shown — never contact details or income.</p>
        <div className="flex items-center gap-3 mb-3"><Switch checked={lb.opt_in} onCheckedChange={(c) => setLb({ ...lb, opt_in: c })} data-testid="leaderboard-toggle" /><span className="text-slate-200 text-sm">Show me on the leaderboard</span></div>
        <Input placeholder="Display name" className={fld} value={lb.display_name} onChange={(e) => setLb({ ...lb, display_name: e.target.value })} data-testid="leaderboard-name" />
        <Button className="mt-3 bg-violet-600 hover:bg-violet-500 text-white" onClick={saveLb} data-testid="save-leaderboard">Save preference</Button>
      </div>
    </div>
  );
}
